import React, { useState, useEffect, useContext, useMemo, useCallback } from "react";
import { useInitCalendarDayMenu } from "../ApplicationsPageHooks";
import { Table, TableBody, Loader, Checkbox, Button, Icon, Popup } from 'semantic-ui-react'
import { UserContext, NotifyContext, PdaContext } from 'context'
import { Card, CardActions, CardBody, CardIcon } from '../../Card'
import ApplicationActionsModal from '../ApplicationsList/ApplicationsActionsModal/ApplicationActionsModal'
import ApplicationsCalendarDayTonnage from './ApplicationsCalendarDayTonnage/ApplicationsCalendarDayTonnage'
import ApplicationCancelModal from '../ApplicationsList/ApplicationsActionsModal/ApplicationCancelModal'
import ApplicationConfirmModal from '../ApplicationsList/ApplicationsActionsModal/ApplicationConfirmModal'
import ApplicationsCalendarDayBlockingModal from './ApplicationsCalendarDayBlockingModal'
// <ApplicationCancelModal applicationsIdList={allCheckedApplicationsIdList} intl={intl} reloadApp={handleDataReloading} isDisabled={!isCancelModalAvailable} withoutLabel={true} />
// <ApplicationConfirmModal reloadApp={handleDataReloading} applicationsIdList={allCheckedApplicationsIdList} action="confirm" intl={intl} isDisabled={!isConfirmModalAvailable} withoutLabel={true} />
// <ApplicationsCalendarDayBlockingModal dayDate={tabState.editCalendarDay} isDayBlocked={isDayBlocked} intl={intl} reloadApp={handleDataReloading} withoutLabel={true} />

import Label from '../../Label'
import Paginator from '../../Paginator'

import api from 'actions/api'
import doRequest from 'actions/doRequest'

import { cardsOnPage } from 'constants/config.json'

import './ApplicationsCalendarDay.css'
import '../ApplicationsList/ApplicationsList.css'
import 'react-date-range/dist/styles.css'; // main css file
import 'react-date-range/dist/theme/default.css'; // theme css file

const ApplicationsCalendarDay = ({ tabState, setTabState, intl, props }) => {
  const targetDate = useMemo(() => new Date(tabState.editCalendarDay), [tabState])
  const [loading, setLoading] = useState(false)
  const [applicationsList, setApplicationsList] = useState([])
  const [page, setPage] = useState(1)
  const [pageCount, setPageCount] = useState(1)
  const [sort, setSort] = useState('byDefault')
  const [options, setOptions] = useState({ cultures: [], warehouses: [], сounterparties: [] })
  const [selectedApplications, setSelectedApplications] = useState([])
  const [completedGrainOperationsData, setCompletedGrainOperationsData] = useState({
    unloading: 0,
    uploading: 0,
    unloadingApproved: 0,
    uploadingApproved: 0,
    unloadingNotApproved: 0,
    uploadingNotApproved: 0
  })
  const [blockedDays, setBlockedDays] = useState([])
  const isDayBlocked = useMemo(() => blockedDays.includes(tabState.editCalendarDay), [blockedDays, tabState.editCalendarDay])

  const { isPda } = useContext(PdaContext)
  const { pushNotify } = useContext(NotifyContext)
  const { logout } = useContext(UserContext)

  const unloadingSelectedSumm = useMemo(() => selectedApplications.filter(app => app.departureType === 'unloading').reduce((acc, app) => {
    if (app?.isChecked) {
      acc = acc + app?.weight;
    }
    return acc
  }, 0), [selectedApplications]);

  const uploadingSelectedSumm = useMemo(() => selectedApplications.filter(app => app.departureType === 'uploading').reduce((acc, app) => {
    if (app?.isChecked) {
      acc = acc + app?.weight;
    }
    return acc
  }, 0), [selectedApplications]);

  const isAllAppsChecked = useMemo(() => selectedApplications.every(app => app?.isChecked), [selectedApplications]);
  const allCheckedApplications = useMemo(() => selectedApplications.filter(app => app?.isChecked), [selectedApplications]);
  const suitableForConfirmation = useMemo(() => allCheckedApplications.filter(app => app?.statusId === 1), [allCheckedApplications]);
  const suitableForCancellation = useMemo(() => allCheckedApplications.filter(app => app?.statusId !== 4 && app?.statusId !== 6), [allCheckedApplications]);
  const allCheckedApplicationsIdList = useMemo(() => allCheckedApplications.map(app => app?.id), [allCheckedApplications]);
  const isConfirmModalAvailable = useMemo(() => allCheckedApplications.length !== 0 && allCheckedApplications.length === suitableForConfirmation.length, [allCheckedApplications.length, suitableForConfirmation.length]);
  const isCancelModalAvailable = useMemo(() => allCheckedApplications.length !== 0 && allCheckedApplications.length === suitableForCancellation.length, [allCheckedApplications.length, suitableForCancellation.length]);

  const isPastDate = useMemo(() => {
    const targetDateMilliseconds = new Date(targetDate).getTime();
    const nowDateMilliseconds = Date.now();
    const differenceInTime = targetDateMilliseconds - nowDateMilliseconds;
    const isDateForToday = new Date(targetDate).getDate() === new Date().getDate();
    return differenceInTime < 0 && !isDateForToday;
  }, [targetDate]);

  const translates = useMemo(() => ({
    notFound: intl.formatMessage({ id: 'title.recordNotFound' }),
    elevator: intl.formatMessage({ id: 'title.elevator' }),
    scheduledDate: intl.formatMessage({ id: 'title.scheduledDate' }),
    contrAgentName: intl.formatMessage({ id: 'title.counterparty' }),
    departureType: intl.formatMessage({ id: 'title.departureType' }),
    culture: intl.formatMessage({ id: 'title.culture' }),
    createdDate: intl.formatMessage({ id: 'sort.byCreatedDate' }),
    confirmedDate: intl.formatMessage({ id: 'sort.byConfirmedDate' }),
    weight: intl.formatMessage({ id: 'title.weight' }),
    status: intl.formatMessage({ id: 'title.status' }),
    warehouses: intl.formatMessage({ id: 'title.elevator' }),
    uploading: intl.formatMessage({ id: 'ordertype.uploading' }),
    unloading: intl.formatMessage({ id: 'ordertype.unloading' }),
    calendarTab: intl.formatMessage({ id: 'msg.tabMenu.calendar' }),
    selectAll: intl.formatMessage({ id: 'btn.selectAll' }),
    unselectAll: intl.formatMessage({ id: 'btn.unselectAll' }),
    applicationsTab: intl.formatMessage({ id: "msg.tabMenu.applications" }),
    cancelApplication: intl.formatMessage({ id: 'btn.cancelApplication' }),
    confirm: intl.formatMessage({ id: 'btn.confirm' })
  }), [intl.locale]) // eslint-disable-line react-hooks/exhaustive-deps

  const loadApplications = useCallback((page = 1) => {
    setApplicationsList([])
    setLoading(true)

    const offset = cardsOnPage * (page - 1), count = cardsOnPage

    return doRequest(api.applications.list(
      offset, count, sort, null, null, null, null, null, null, null, null, null, null, null, targetDate), pushNotify, logout)
      .ready.then(data => {
        const { applications, count } = data
        const pages = Math.ceil(count / cardsOnPage)

        setApplicationsList(applications)

        if (page > pages && pages > 0) {
          loadApplications(pages)
        } else {
          setPageCount(pages)
          setPage(page)
        }
      }).catch(e => {
        console.log('e', e);
        setApplicationsList([])
      }).finally(() => {
        setLoading(false)
      })
  }, [logout, pushNotify, sort, targetDate])

  const onConfirmEditModal = async (data) => {
    try {
      if (!data.scheduledDate) {
        pushNotify('error', 'title.error', 'msg.dateMustBeFilled', 2000)
        return
      }

      if (!data.weight) {
        pushNotify('error', 'title.error', 'msg.weightMustBeFilled', 2000)
        return
      }

      if (data?.weight < 1 || isNaN(data?.weight)) {
        pushNotify('error', 'title.error', 'msg.weightMustBeGreaterThanZero', 2000)
        return
      }

      const response = await doRequest(api.suggestedEdits.add(data), pushNotify, logout).ready;
      if (response?.text === 'success') {
        pushNotify('success', 'title.success', 'msg.sendRequstSuccess', 2000);
      }
      await loadApplications(page);
      await getCompletedGrainOperationsData();
    } catch (error) { console.log(error); }
  }

  const getOptions = () => {
    let requests = []
    requests.push({ statuses: [{ id: 1, name: 2 }] })
    requests.push(doRequest(api.applicationsStatuses.list(), pushNotify, logout).ready)
    requests.push(doRequest(api.elevators.list(), pushNotify, logout).ready)
    requests.push(doRequest(api.cultures.list(), pushNotify, logout).ready)

    return Promise.all(requests).then(result => {
      const res = result.reduce((acc, data) => {
        if (data.elevators) return { ...acc, warehouses: data.elevators.map((elevator) => { return { key: elevator.id, value: elevator.id, text: elevator.name } }) };
        if (data.cultures) return { ...acc, cultures: data.cultures.map((culture) => { return { key: culture.id, value: culture.id, text: intl.formatMessage({ id: `cultures.${culture.title}` }) } }) };
        if (data.statuses) return {
          ...acc, statuses: data.statuses.map((status) => {
            return { key: status.id, value: status.id, text: intl.formatMessage({ id: `title.application.status.${status.title}` }) }
          })
        };
        return acc
      }, {})
      setOptions({ ...res })
    }).catch(console.error)
  }

  const getCompletedGrainOperationsData = useCallback(() => {
    return Promise.resolve(doRequest(api.completedGrainOperationsData.list(targetDate), pushNotify, logout).ready).then(result => {
      const res = result?.waybillsTonnage[0]
      setCompletedGrainOperationsData({ ...res })
    }).catch(console.error)
  }, [logout, pushNotify, targetDate])

  const getBlockedDays = useCallback(() => {
    return Promise.resolve(doRequest(api.blockedDays.list(), pushNotify, logout).ready).then(res => setBlockedDays(res?.blockedDays)).catch(console.error)
  }, [logout, pushNotify])

  const handleDataReloading = useCallback(async () => {
    await loadApplications(page);
    await getCompletedGrainOperationsData();
    await getBlockedDays();

  }, [getBlockedDays, getCompletedGrainOperationsData, loadApplications, page])

  useEffect(() => {
    const fetchData = async () => {
      await getOptions();
      await loadApplications(1);
      await getCompletedGrainOperationsData();
      await getBlockedDays();
    }
    fetchData();
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (applicationsList.length > 0) {
      setSelectedApplications(applicationsList.map(app => ({ id: app.id, weight: app.weight, departureType: app.departureType, statusId: app.statusId, isChecked: false })))
    } else {
      setSelectedApplications([])
    }
  }, [applicationsList])


  const handleCheckboxClick = (id) => {
    setSelectedApplications(selectedApplications.map(app => app.id === id ? { ...app, isChecked: !app?.isChecked } : app))
  }

  const renderBtnForCheckboxes = (isAllAppsChecked) => {
    return (
      <>
        {isAllAppsChecked ?
          // <Button className="selectCheckboxesButton" onClick={() => setSelectedApplications(selectedApplications.map(app => ({ ...app, isChecked: false })))} >
          //   <span>{translates.unselectAll}</span>
          // </Button>
          <Popup inverted content={translates.unselectAll} trigger={<Button className="actionButton table-icon" icon onClick={() => setSelectedApplications(selectedApplications.map(app => ({ ...app, isChecked: false })))} > <Icon name="square outline" /></Button>} />
          :
          // <Button className="selectCheckboxesButton" onClick={() => setSelectedApplications(selectedApplications.map(app => ({ ...app, isChecked: true })))} >
          //   <span>{translates.selectAll}</span>
          // </Button>
          <Popup inverted content={translates.selectAll} trigger={<Button className="actionButton table-icon" icon onClick={() => setSelectedApplications(selectedApplications.map(app => ({ ...app, isChecked: true })))} > <Icon name="check square" /></Button>} />
        }
      </>
    )
  }

  const setTab = useCallback(
    (tab) => { setTabState((tabState) => ({ ...tabState, tab })) },
    [setTabState]);

  const menuList = useMemo(() => {
    const calendarList = [
      {
        id: 5,
        popUpText: translates.calendarTab,
        iconName: "calendar alternate outline",
        actions: { onClick: () => setTab(2) },
      },
      {
        id: 6,
        popUpText: translates.applicationsTab,
        iconName: "list",
        actions: { onClick: () => setTab(1) },
      }
    ];

    const actionsList = [
      {
        node: <ApplicationsCalendarDayBlockingModal key={'ApplicationsCalendarDayBlockingModal'} dayDate={tabState.editCalendarDay} isDayBlocked={isDayBlocked} intl={intl} reloadApp={handleDataReloading} withoutLabel={true} />
      },
      {
        node: isConfirmModalAvailable ?
          <ApplicationConfirmModal key={'ApplicationConfirmModal'} reloadApp={handleDataReloading} applicationsIdList={allCheckedApplicationsIdList} action="confirm" intl={intl} withoutLabel={true} />
          :
          <Popup key={'ApplicationConfirmModal'} inverted content={translates.confirm} trigger={<Button className="actionButton inactivated" icon> <Icon disabled={true} name="check circle" /></Button>} />
      },
      {
        node: isCancelModalAvailable ?
          <ApplicationCancelModal key={'ApplicationCancelModal'} applicationsIdList={allCheckedApplicationsIdList} intl={intl} reloadApp={handleDataReloading} withoutLabel={true} />
          :
          <Popup key={'ApplicationCancelModal'} inverted content={translates.cancelApplication} trigger={<Button className="actionButton inactivated" icon> <Icon disabled={true} name="undo" /></Button>} />
      }
    ];

    return isPastDate ?
      calendarList
      :
      [...actionsList, ...calendarList];
  }, [allCheckedApplicationsIdList, handleDataReloading, intl, isCancelModalAvailable, isConfirmModalAvailable, isDayBlocked, isPastDate, setTab, tabState.editCalendarDay, translates.applicationsTab, translates.calendarTab])

  const currentDayTitle = useMemo(() => {
    return tabState.editCalendarDay + ' ' + intl.formatMessage({ id: `title.day.${new Date(tabState.editCalendarDay).getDay()}` });
  }, [intl, tabState.editCalendarDay]);

  useInitCalendarDayMenu({ tabState: { ...tabState, menuList, calendarDayTitle: currentDayTitle }, setTabState, intl, allCheckedApplications, isDayBlocked });

  return (
    <React.Fragment>
      {loading ?
        <Loader className={"residue-loader"} active inline="centered" />
        :
        applicationsList.length === 0 ?
          <Label text={translates.notFound} />
          :
          isPda ?
            <>
              {applicationsList.map((application) => (
                <Card key={application.id} className="departureCard">
                  <CardIcon name="truck" />
                  <CardBody>
                    <span><b>{translates.contrAgentName}:</b> {application.contrAgentName}</span>
                    <span><b>{translates.departureType}:</b> {translates[application.departureType]}</span>
                    <span><b>{translates.culture}:</b> {intl.formatMessage({ id: `cultures.${application.nomenclature}` })}</span>
                    <span><b>{translates.weight}:</b> {application.weight}</span>
                    <span><b>{translates.status}:</b> {intl.formatMessage({ id: `title.application.status.${application.status}` })}</span>
                    <CardActions>
                      <ApplicationActionsModal application={application} onConfirmEditModal={onConfirmEditModal} options={options} reloadApp={handleDataReloading} />
                      {!isPastDate && <Checkbox key={application.id} name={`application${application.id}`} className="checkbox item" onChange={() => handleCheckboxClick(application.id)} checked={Boolean(selectedApplications?.find((selectedApp) => ((selectedApp.id === application.id) && selectedApp.isChecked)))} />}
                    </CardActions>
                  </CardBody>
                </Card>
              ))}
              <ApplicationsCalendarDayTonnage intl={intl} completedGrainOperationsData={completedGrainOperationsData} isPastDate={isPastDate} unloadingSelectedSumm={unloadingSelectedSumm} uploadingSelectedSumm={uploadingSelectedSumm} />
            </>
            :
            <>
              <Table celled striped >
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>{translates.contrAgentName}</Table.HeaderCell>
                    <Table.HeaderCell>{translates.departureType}</Table.HeaderCell>
                    <Table.HeaderCell>{translates.culture}</Table.HeaderCell>
                    <Table.HeaderCell>{translates.weight}</Table.HeaderCell>
                    <Table.HeaderCell>{translates.status}</Table.HeaderCell>
                    <Table.HeaderCell></Table.HeaderCell>
                    {!isPastDate && <Table.HeaderCell textAlign='center' >{renderBtnForCheckboxes(isAllAppsChecked)}</Table.HeaderCell>}
                  </Table.Row>
                </Table.Header>
                <TableBody>
                  {
                    applicationsList.map((application) => (
                      <Table.Row key={application.id} className={application.crash ? 'red' : ''}>
                        <Table.Cell>{application.contrAgentName}</Table.Cell>
                        <Table.Cell>{translates[application.departureType]}</Table.Cell>
                        <Table.Cell>{intl.formatMessage({ id: `cultures.${application.nomenclature}` })}</Table.Cell>
                        <Table.Cell>{application.weight}</Table.Cell>
                        <Table.Cell>{intl.formatMessage({ id: `title.application.status.${application.status}` })}</Table.Cell>
                        <Table.Cell className="actionColumn"><ApplicationActionsModal application={application} onConfirmEditModal={onConfirmEditModal} options={options} reloadApp={handleDataReloading} /></Table.Cell>
                        {!isPastDate && <Table.Cell className="actionColumn" textAlign='center'><Checkbox key={application.id} name={`application${application.id}`} className="checkbox item" onChange={() => handleCheckboxClick(application.id)} checked={Boolean(selectedApplications?.find((selectedApp) => ((selectedApp.id === application.id) && selectedApp.isChecked)))} /></Table.Cell>}
                      </Table.Row>
                    ))
                  }
                </TableBody>
              </Table>
              <ApplicationsCalendarDayTonnage intl={intl} completedGrainOperationsData={completedGrainOperationsData} isPastDate={isPastDate} unloadingSelectedSumm={unloadingSelectedSumm} uploadingSelectedSumm={uploadingSelectedSumm} />
            </>
      }
      <Paginator pageCount={pageCount} page={page} setPage={loadApplications} disabled={loading} />
    </React.Fragment>
  );
};

export default ApplicationsCalendarDay;
